import { convertDDMMMYYYYtoDate } from "../components/utils/date-formatter-utils";

/**
 * Validates an email address.
 *
 * @param value - The email address to validate.
 * @returns An error message if the email is invalid, otherwise an empty string.
 */
export const validateEmail = (value: string): string => {
    if (!value) {
        return "Email is required";
    }
    if (!/\S+@\S+\.\S+/.test(value)) {
        return "Invalid email format";
    }
    return "";
};

/**
 * Validates a text value.
 *
 * @param value - The text value to validate.
 * @returns An error message if the text is empty, otherwise an empty string.
 */
export const validateText = (value: string): string => {
    if (value.trim() === "") {
        return "Text is required";
    }
    return "";
};

/**
 * Validates an address value.
 *
 * @param value - The address value to validate.
 * @returns An error message if the address is invalid, otherwise an empty string.
 */
export const validateAddress = (value: string): string => {
    if (!value || value.trim() === "") {
        return "";
    }

    const addressParts = value.split(",");

    if (addressParts.length !== 3) {
        return "Address format is invalid. Please provide three parts [country, state, region] separated by commas";
    }

    const [country, state, region] = addressParts.map((part) => part.trim());

    if (!country || !state || !region) {
        return "Address format is invalid. Each part [country, state, region] must not be empty";
    }

    return "";
};

/**
 * Validates a decimal value.
 *
 * @param value - The decimal value to validate.
 * @returns An error message if the decimal value is invalid, otherwise an empty string.
 */
export const validateDecimal = (value: string): string => {
    if (!value) {
        return "Value is required";
    }
    if (!/^(-)?\d+(\.\d+)?$/.test(value)) {
        return "Invalid decimal format";
    }
    return "";
};

/**
 * Validates an integer value.
 *
 * @param value - The integer value to validate.
 * @returns An error message if the integer value is invalid, otherwise an empty string.
 */
export const validateInteger = (value: string): string => {
    if (!value) {
        return "Value is required";
    }
    if (!/^(-)?\d+$/.test(value)) {
        return "Invalid integer format";
    }
    return "";
};


/**
 * Validates an hour value.
 *
 * @param value - The hour value to validate.
 * @returns An error message if the hour value is invalid, otherwise an empty string.
 */
export const validateHours = (value: string): string => {
    if (!value) {
        return "Value is required";
    }
    if (!/^-?\d+(\.\d+)?$/.test(value)) { // Updated regex to allow decimals
        return "Hour value must be a valid number";
    }
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
        return "Invalid hour format";
    }
    if (numericValue < -23 || numericValue > 23) {
        return "Hour value must be between -23 and 23";
    }
    return "";
};

export const validateFile = (value: string): string => {
    if (!value) {
        return "File is required";
    }
    return "";
};

export const validateSiteLongitudeLatitudeDecimal = (value: string): string => {
    if (!value) {
        return "";
    }
    if (!/^(-)?\d+(\.\d+)?$/.test(value)) {
        return "Invalid decimal format";
    }
    return "";
};

/**
 * Validates a hexadecimal value with 16 digits.
 *
 * @param value - The hexadecimal value to validate.
 * @returns An error message if the value is not a 16-digit hexadecimal number, otherwise an empty string.
 */
export const validateHexadecimal16Digit = (value: string): string => {
    const hex16DigitRegex = /^[0-9A-Fa-f]{16}$/;
    if (!hex16DigitRegex.test(value)) {
        return "The value must be a 16-digit hexadecimal number.";
    }
    return "";
};

/**
 * Validates a date string in 12-Jan-2022 format.
 *
 * @param dateString - The date string to validate.
 * @returns A boolean indicating whether the date string is valid.
 */
export const isValidDateDDMMMYYYY = (dateString: string): boolean => {
    const dateRegex = /^(\d{2})\-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\-(\d{4})$/i;
    const match = dateRegex.exec(dateString);
    if (match) {
        const date = convertDDMMMYYYYtoDate(dateString);
        if (date) {
            return true;
        }
    }
    return false;
};

/**
 * Validates a select value.
 *
 * @param value - The select value to validate.
 * @returns An error message if the select value is empty or invalid, otherwise an empty string.
 */
export const validateSelect = (value: string | undefined): string => {
    if (!value || value.trim() === "") {
        return "Selection is required";
    }
    return "";
};
